import { createContext, useContext } from 'react'
import { MissedCallsTableColumn } from '~/pages/MissedCalls/data/MissedCallsTableData'
import { Size } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

const missedCallsTableStyleParts = [
  MissedCallsTableColumn.DateTime,
  MissedCallsTableColumn.From,
  MissedCallsTableColumn.To,
  MissedCallsTableColumn.FollowUpTime,
  MissedCallsTableColumn.Type,
  MissedCallsTableColumn.FollowUpStatus,
  MissedCallsTableColumn.AssignedTo,
  MissedCallsTableColumn.Action,
] as const

const helpers = createMultiStyleConfigHelpers(missedCallsTableStyleParts)

export const MissedCallsTableStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    dateTimeColumn: {
      transition: 'all 0.4s ease-in-out',
    },
    followUpTimeColumn: {
      transition: 'all 0.15s ease-in-out',
    },
    toColumn: {
      transition: 'all 0.15s ease-in-out',
    },
    fromColumn: {
      transition: 'all 0.15s ease-in-out',
    },
    typeColumn: {
      transition: 'all 0.15s ease-in-out',
    },
    followUpStatusColumn: {
      transition: 'all 0.15s ease-in-out',
    },
    assignedToColumn: {
      transition: 'all 0.15s ease-in-out',
    },
    actionColumn: {
      transition: 'all 0.15s ease-in-out',
    },
  },
  sizes: {
    [Size.sm]: {
      dateTimeColumn: {
        minWidth: '6.625rem',
        maxWidth: '7rem',
      },
      followUpTimeColumn: {
        minWidth: '5rem',
        maxWidth: '6rem',
      },
      toColumn: {
        minWidth: '8rem',
      },
      fromColumn: {
        minWidth: '8rem',
      },
      typeColumn: {
        minWidth: '5rem',
        maxWidth: '6rem',
      },
      followUpStatusColumn: {
        minWidth: '8rem',
      },
      assignedToColumn: {
        minWidth: '8rem',
      },
      actionColumn: {
        minWidth: '5rem',
        maxWidth: '6rem',
      },
    },
    [Size.lg]: {
      dateTimeColumn: {
        minWidth: '6.625rem',
        maxWidth: '12.5rem',
      },
      followUpTimeColumn: {
        minWidth: '9rem',
        maxWidth: '10rem',
      },
      toColumn: {
        minWidth: '8.75rem',
      },
      fromColumn: {
        minWidth: '8.75rem',
      },
      typeColumn: {
        minWidth: '9rem',
        maxWidth: '10rem',
      },
      followUpStatusColumn: {
        minWidth: '10.5rem',
      },
      assignedToColumn: {
        minWidth: '9.5rem',
      },
      actionColumn: {
        minWidth: '6rem',
        maxWidth: '8rem',
      },
    },
    [Size.md]: {
      dateTimeColumn: {
        minWidth: '6.625rem',
        maxWidth: '10rem',
      },
      followUpTimeColumn: {
        minWidth: '8.75rem',
        maxWidth: '9.75rem',
      },
      toColumn: {
        minWidth: '8.5rem',
      },
      fromColumn: {
        minWidth: '8.5rem',
      },
      typeColumn: {
        minWidth: '8.5rem',
        maxWidth: '9.5rem',
      },
      followUpStatusColumn: {
        minWidth: '10rem',
      },
      assignedToColumn: {
        minWidth: '9.25rem',
      },
      actionColumn: {
        minWidth: '6rem',
        maxWidth: '8rem',
      },
    },
  },
})

export const useMissedCallsTableStyles = (
  props?: StyleThemingProps,
): MultiStyleType<typeof missedCallsTableStyleParts> => {
  return mergeMultiStyleConfig(MissedCallsTableStyle, props) as MultiStyleType<
    typeof missedCallsTableStyleParts
  >
}

// Create a React context for sharing table styles between parent and child components
const MissedCallsTableStylesContext = createContext<
  MultiStyleType<typeof missedCallsTableStyleParts> | undefined
>(undefined)

export const MissedCallsTableStylesProvider =
  MissedCallsTableStylesContext.Provider

export const useMissedCallsTableStylesContext = () => {
  const value = useContext(MissedCallsTableStylesContext)
  if (value === undefined) {
    throw new Error(
      'useMissedCallsTableStylesContext must be used within MissedCallsTableStylesProvider',
    )
  }
  return value
}

const tableHeaderCellParts = [
  'container',
  'tableHeader',
  'headerColumnWrapper',
  'headerText',
  'sortIcon',
  'listContainer',
  MissedCallsTableColumn.DateTime,
  MissedCallsTableColumn.From,
  MissedCallsTableColumn.To,
  MissedCallsTableColumn.FollowUpTime,
  MissedCallsTableColumn.Type,
  MissedCallsTableColumn.FollowUpStatus,
  MissedCallsTableColumn.AssignedTo,
  MissedCallsTableColumn.Action,
] as const

const cellHelpers = createMultiStyleConfigHelpers(tableHeaderCellParts)

export const MissedCallsTableHeaderCellStyle =
  cellHelpers.defineMultiStyleConfig({
    baseStyle: {
      container: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 0',
        pt: '1.25rem',
        pl: '1.25rem',
        pr: '.25rem',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        userSelect: 'none',
        backgroundColor: 'bg.subtle',
      },
      tableHeader: {
        display: 'flex',
        height: '2.5rem',
        pl: '1rem',
        pr: '2.25rem',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '0.625rem',
        alignSelf: 'stretch',
      },
      headerColumnWrapper: {
        display: 'flex',
        height: '2.5rem',
        alignItems: 'center',
        flexShrink: 0,
        alignSelf: 'stretch',
        borderBottom: '1px solid',
        borderBottomColor: 'border.default',
      },
      dateTimeColumn: {
        display: 'flex',
        flex: '1 0 0',
        alignItems: 'center',
        alignSelf: 'stretch',
        py: '0.75rem',
        pr: '1rem',
        gap: '0.5rem',
      },
      followUpTimeColumn: {
        display: 'flex',
        flex: '1 0 0',
        alignItems: 'center',
        alignSelf: 'stretch',
        py: '0.75rem',
        pl: '0',
        pr: '0.75rem',
        gap: '0.75rem',
      },
      toColumn: {
        display: 'flex',
        flex: '1 0 0',
        alignItems: 'center',
        alignSelf: 'stretch',
        py: '0.75rem',
        gap: '0.75rem',
      },
      fromColumn: {
        display: 'flex',
        flex: '1 0 0',
        alignItems: 'center',
        alignSelf: 'stretch',
        py: '0.75rem',
        gap: '0.75rem',
      },
      typeColumn: {
        display: 'flex',
        flex: '1 0 0',
        alignItems: 'center',
        alignSelf: 'stretch',
        py: '0.75rem',
        pl: '1rem',
        pr: '0.75rem',
        gap: '0.75rem',
      },
      followUpStatusColumn: {
        display: 'flex',
        flex: '1 0 0',
        alignItems: 'center',
        alignSelf: 'stretch',
        py: '0.75rem',
        pl: 0,
        pr: 0,
        gap: '0.75rem',
      },
      assignedToColumn: {
        display: 'flex',
        flex: '1 0 0',
        alignItems: 'center',
        alignSelf: 'stretch',
        py: '0.75rem',
        gap: '0.75rem',
      },
      actionColumn: {
        display: 'flex',
        flex: '0 1 0',
        alignItems: 'center',
        alignSelf: 'stretch',
        py: '0.75rem',
        gap: '0.75rem',
      },
      headerText: {
        ...textStyles[textStyleKeys.Body3_Bold],
        color: 'fg.default',
      },
      listContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: '1 0 0',
        alignSelf: 'stretch',
        height: 'full',
        overflow: 'hidden',
        backgroundColor: 'bg.muted',
      },
      sortIcon: {
        boxSize: '10px',
        color: 'fg.default',
      },
    },
    sizes: {
      [Size.sm]: {
        container: {
          paddingLeft: '1rem',
        },
        tableHeader: {
          paddingLeft: '1rem',
          paddingRight: '1.25rem',
        },
      },
    },
  })

export const useMissedCallsTableHeaderCellStyles = (
  props?: StyleThemingProps,
): MultiStyleType<typeof tableHeaderCellParts> => {
  return mergeMultiStyleConfig(
    MissedCallsTableHeaderCellStyle,
    props,
  ) as MultiStyleType<typeof tableHeaderCellParts>
}
